import { Provider, ApiResponse } from '../types/api';
import { API_CONSTANTS } from '../constants/api-constants';
import axios from 'axios';
import { AxiosError } from 'axios';
import { getValidMaiaAccessToken, tenantHeaderFor } from './auth/brokerSession';

// Custom error interface for API errors
interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
    status?: number;
  };
  config?: {
    url?: string;
    method?: string;
  };
}

export const providerService = {
  // Fetch billing providers
  async getBillingProviders(
    tenantId: string,
  ): Promise<ApiResponse<Provider[]>> {
    try {
      const token = await getValidMaiaAccessToken();
      const response = await axios.get<ApiResponse<Provider[]>>(
        `${API_CONSTANTS.RULES_BASE_URL}/providers/BILLING`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...tenantHeaderFor(tenantId),
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to fetch billing providers';

      const customError = new Error(errorMessage) as ApiError;
      customError.response = axiosError?.response;
      customError.config = axiosError?.config;

      throw customError;
    }
  },

  // Fetch rendering providers
  async getRenderingProviders(
    tenantId: string,
  ): Promise<ApiResponse<Provider[]>> {
    try {
      const token = await getValidMaiaAccessToken();
      const response = await axios.get<ApiResponse<Provider[]>>(
        `${API_CONSTANTS.RULES_BASE_URL}/providers/RENDERING`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...tenantHeaderFor(tenantId),
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to fetch rendering providers';

      const customError = new Error(errorMessage) as ApiError;
      customError.response = axiosError?.response;
      customError.config = axiosError?.config;

      throw customError;
    }
  },

  // Fetch supervising providers
  async getSupervisingProviders(
    tenantId: string,
  ): Promise<ApiResponse<Provider[]>> {
    try {
      const token = await getValidMaiaAccessToken();
      const response = await axios.get<ApiResponse<Provider[]>>(
        `${API_CONSTANTS.RULES_BASE_URL}/providers/SUPERVISING`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...tenantHeaderFor(tenantId),
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to fetch supervising providers';

      const customError = new Error(errorMessage) as ApiError;
      customError.response = axiosError?.response;
      customError.config = axiosError?.config;

      throw customError;
    }
  },
};
